<script setup lang="ts">
// A pane card with a discreet "show me this at full size" control. The card
// itself is passed as the slot; while enlarged, this wrapper's `Teleport` moves
// it into the overlay layer's host, so the card stays the same component
// instance and keeps its charts and its drill-down clicks.
//
// Only for the cards that have no expander of their own (the report panels
// carry one in their head); the population cards are plain sections, so they
// are wrapped here.
import { computed, onBeforeUnmount, useId } from "vue";

import {
	ANALYSIS_CARD_HOST_ID,
	cardHostGeneration,
	cardHostMounted,
	collapseCard,
	expandCard,
	expandedCardId,
} from "./cardExpander";

defineProps<{
	/** What the card shows, in lowercase, read out as "Open … at full size". */
	label: string;
}>();

/** Per-instance id, so `expandedCardId` names exactly one card; `useId` never
 *  collides between the pane's several cards. */
const id = `card-${useId()}`;

const isOpen = computed(() => expandedCardId.value === id);

// Switching questions unmounts the whole population block. A card that is
// enlarged at that moment would leave the overlay open over an empty host,
// because nothing else clears the id — so it closes itself first.
onBeforeUnmount(() => {
	if (isOpen.value) collapseCard();
});
</script>

<template>
	<div class="expandable-card">
		<!-- Shown only when the layer's host exists (a card elsewhere would
		     teleport into nothing and vanish) and only while this card is not
		     already the enlarged one. -->
		<button
			v-if="cardHostMounted && !isOpen"
			type="button"
			class="expandable-toggle"
			:title="`Open ${label} at full size`"
			:aria-label="`Open ${label} at full size`"
			@click="expandCard(id)"
		>
			<i class="bi bi-arrows-angle-expand"></i>
		</button>

		<!-- Teleported only while enlarged: the overlay host is a persistent
		     element inside the layer, so moving the DOM there keeps this a live
		     instance. `defer` matters: this card mounts before the layer that
		     owns the host does, and a Teleport resolves its target at mount — it
		     would cache a missing host and then throw when it is finally
		     enabled. Deferring resolves the target after the whole tree has
		     rendered, when the layer's host exists. The key rebuilds the
		     teleport when a different layer mounts, because the cached target
		     would otherwise be that layer's removed host. -->
		<Teleport
			:key="cardHostGeneration"
			defer
			:to="'#' + ANALYSIS_CARD_HOST_ID"
			:disabled="!isOpen"
		>
			<slot />
		</Teleport>
	</div>
</template>

<style scoped>
.expandable-card {
	position: relative;
	min-width: 0;
	/* The pane's slot is a cell grid and each wrapped panel carries
	   `grid-column: 1 / -1` itself; the wrapper is the grid child now, so it
	   has to span the row too or the panel would be squeezed into one cell. */
	grid-column: 1 / -1;
}

/* Muted and small: it is an affordance, not part of the reading. Top-right is
   free — every wrapped card puts its title at the top-left. */
.expandable-toggle {
	position: absolute;
	top: 6px;
	right: 6px;
	z-index: 1;
	border: 0;
	background: transparent;
	padding: 0 2px;
	line-height: 1;
	font-size: 0.72rem;
	color: #6c757d;
	cursor: pointer;
}

.expandable-toggle:hover {
	color: #0d6efd;
}
</style>
